import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch, AnyAction } from 'redux';
import * as _ from 'underscore';
import { Messages } from '../../../gen-src/Localize';
import { AppUrls } from '../../app/AppUrls';
import { appUrlsSelector } from '../../app/selectors/AppUrlsSelector';
import { AppState } from '../../app/state/AppState';
import * as Layout from '../../common/styles/Layout';
import { ContentDefinitions } from '../models/contentDefinitions';
import { DisplayMode } from '../models/contentDefinitions/IContentDefinition';
import { ContentType } from '../models/ContentType';
import { PageType } from '../models/PageType';
import { MixedContentTypeSearchResult, SearchResultItem } from '../models/SearchResultTypes';
import { ConnectedCardView } from './CardView/CardView';
import { ConnectedListView } from './listView/ListView';
import { ListViewStyles } from './listView/ListViewStyles';
import { updateMetricsData } from '../actions/MetricsDataUpdateAction';
import { SiteUrlsContext } from '@tableau/catalog-ui-temp';
import { SessionManagementService } from '../services/SessionManagementService';
import { DisplayBy, DisplaySelection } from '../models/DisplaySelection';

export interface ResultListWrapperOwnProps {
  readonly contentType: ContentType;
  readonly items: SearchResultItem[];
  readonly pageType: PageType;
}

export const ThumbnailDisplayTestID = 'ThumbnailDisplay';
export const ListDisplayTestID = 'ListDisplay';

export type ResultListWrapperProps = ResultListWrapperOwnProps & ResultListWrapperStateProps & ResultListDispatchProps;

export const ResultListWrapperContentTestID = 'ResultListWrapperContent';

export function ResultListWrapper(props: ResultListWrapperProps): JSX.Element {
  const getDisplayMode = (): DisplayMode => {
    const contentType: ContentType = props.contentType;

    return props.displayBy?.name === DisplayBy.getMixedViewDisplaySelection().name
      ? ContentDefinitions[contentType].defaultDisplayMode
      : 'list';
  };

  if (props.contentType === ContentType.metric) {
    const ids = props.items.filter((item) => item).map((m) => m.id);
    props.updateMetricsData(ids);
  }

  const renderResultListWrapper = (displayMode: DisplayMode): JSX.Element => {
    if (displayMode === 'thumbnail') {
      return (
        <ConnectedCardView
          data-tb-test-id={ThumbnailDisplayTestID}
          contentType={props.contentType}
          items={props.items}
          pageType={props.pageType}
        />
      );
    } else if (displayMode === 'list') {
      if (ContentDefinitions[props.contentType].getListViewColumns()) {
        const renderListView = () => {
          return (
            <ConnectedListView
              urls={props.urls}
              items={props.items}
              contentFilter={[]}
              currentSort={[]}
              changeSort={_.noop}
              setSortOrder={_.noop}
              isSessionSiteOrServerAdmin={
                SessionManagementService.instance.isSessionServerAdmin() || SessionManagementService.instance.isSessionSiteAdmin()
              }
              isGuestEnabled={false}
              contentType={props.contentType}
            />
          );
        };

        if (props.pageType === PageType.single) {
          return renderListView();
        }

        return (
          <div className={Layout.relative} style={{ height: calculateListViewHeight(props.items.length) + 'px' }}>
            {renderListView()}
          </div>
        );
      } else {
        return (
          <ul data-tb-test-id={ListDisplayTestID}>
            {props.items.map((item, index) => (
              <li key={index}>{item ? item.name : ''}</li>
            ))}
          </ul>
        );
      }
    }
    return <div>{Messages.ErrorWithDisplay()}</div>;
  };

  const displayMode = getDisplayMode();

  return (
    <div data-tb-test-id={ResultListWrapperContentTestID + '-' + props.contentType}>
      <SiteUrlsContext.Provider value={props.urls}>{renderResultListWrapper(displayMode)}</SiteUrlsContext.Provider>
    </div>
  );

  function calculateListViewHeight(itemCount: number): number {
    // Since currently the data grid inside list view doesn't support auto height based on item count,
    // instead, according to Shared Widget team's suggestion, we have to give it an explicite height by manually calculating the height.
    const listViewItemHeight = ListViewStyles.LIST_VIEW_ROW_OUTER_HEIGHT;
    const listViewHeaderHeight = listViewItemHeight + 16;
    return listViewHeaderHeight + itemCount * listViewItemHeight;
  }
}

export interface ResultListWrapperStateProps {
  urls: AppUrls;
  mixedContentTypeSearchResults?: MixedContentTypeSearchResult;
  displayBy?: DisplaySelection;
}

export interface ResultListDispatchProps {
  readonly updateMetricsData: (ids: string[]) => void;
}

const mapStateToProps = (state: AppState) => {
  return {
    urls: appUrlsSelector(),
    mixedContentTypeSearchResults: state.search.mixedContentType.searchResult,
    displayBy: state.search.displayBy,
  };
};
const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) =>
  bindActionCreators(
    {
      updateMetricsData: updateMetricsData,
    },
    dispatch
  );

export const ConnectedResultListWrapper: React.ComponentClass<ResultListWrapperOwnProps> = connect(
  mapStateToProps,
  mapDispatchToProps
)(ResultListWrapper);
